/**
 * Anzeigetext einer Benachrichtigung ohne Emojis (Oktober 2026, iOS 1.0.3).
 *
 * Die Server-Texte sind seit dem Polishing emojifrei. Alte Datensätze in der
 * Datenbank bleiben aber unverändert („Session-Einladung! 📚“, „Session
 * startet bald! ⏰“), und sie erscheinen nur in der Benachrichtigungsliste.
 * Deshalb wird genau hier beim Anzeigen bereinigt, an einer einzigen Stelle
 * (NotificationRow). Toasts und native Pushes tragen nur neue Texte.
 *
 * - Titel: alle Emoji-Cluster entfernen.
 * - Text: nur Emoji-Cluster am Anfang und am Ende. Emojis mitten im Text
 *   stammen oft aus zitierten Session-Themen oder Skill-Namen, die
 *   Nutzer:innen selbst so geschrieben haben.
 * - Der Otter 🦦 bleibt immer (CD-Ausnahme).
 * - Ausrufezeichen alter Texte bleiben stehen: bereinigt werden nur Emojis.
 *
 * Ein naives \p{Extended_Pictographic} würde auch ©, ™, ↔, ▶ und ✓ löschen.
 * Die Regex trifft solche Zeichen nur mit Variation Selector 16 (U+FE0F),
 * also in ihrer Emoji-Darstellung, und nimmt Hautton, ZWJ-Folgen, Flaggen
 * und Tastenkappen als ein Cluster.
 */

const CLUSTER_SOURCE =
  '(?:\\p{Regional_Indicator}{2}' +
  '|[#*0-9]\\uFE0F?\\u20E3' +
  '|(?:\\p{Emoji_Presentation}|\\p{Extended_Pictographic}\\uFE0F)\\p{Emoji_Modifier}?' +
  '(?:\\u200D(?:\\p{Emoji_Presentation}|\\p{Extended_Pictographic}\\uFE0F?)\\p{Emoji_Modifier}?)*)';

const EMOJI = new RegExp(CLUSTER_SOURCE, 'gu');
const LEADING = new RegExp(`^${CLUSTER_SOURCE}`, 'u');
const TRAILING = new RegExp(`${CLUSTER_SOURCE}$`, 'u');
const OTTER = '\u{1F9A6}';

const tidy = (s) => s
  .replace(/[ \t]{2,}/g, ' ')
  .replace(/[ \t]+([.,!?;:)])/g, '$1')
  .trim();

/** Titel: jedes Emoji-Cluster außer dem Otter entfernen. */
export const stripEmojis = (s) => {
  if (typeof s !== 'string') return s;
  return tidy(
    s
      .replace(EMOJI, (m) => (m === OTTER ? m : ''))
      .replace(/[️‍]/g, ''),
  );
};

/** Text: nur Emoji-Cluster am Anfang und am Ende entfernen, den Otter behalten. */
export const stripEdgeEmojis = (s) => {
  if (typeof s !== 'string') return s;
  let out = s.trim();
  let changed = true;
  while (changed) {
    changed = false;
    const lead = out.match(LEADING);
    if (lead && lead[0] !== OTTER) {
      out = out.slice(lead[0].length).trimStart();
      changed = true;
    }
    const trail = out.match(TRAILING);
    if (trail && trail[0] !== OTTER) {
      out = out.slice(0, out.length - trail[0].length).trimEnd();
      changed = true;
    }
  }
  return out.replace(/[ \t]+([.,!?;:)])$/, '$1');
};

/**
 * Liefert den Anzeigetext einer Benachrichtigung.
 * @param {{ title?: string, message?: string }} n
 * @returns {{ title: string, message: string }}
 */
export const getNotificationText = (n) => ({
  title: stripEmojis(n?.title ?? ''),
  message: stripEdgeEmojis(n?.message ?? ''),
});

export default getNotificationText;
